import { FileData } from 'api/file/file.types';
import { uploadFile } from 'api/file/file';
import { FC, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { themeable } from 'themes/utils';
import { FILE_ACCEPT_VALUES } from 'ui/FileButton/constants';
import { FileType } from 'ui/FileButton/FileButton.types';
import { Headline } from 'ui/Styled/Styled';
import { imgFileUrl } from 'utils/cdn';
import { toast } from 'react-toastify';

export type FileCloudProps = {
    accept?: FileType;
    onUpload(file: FileData): void;
    isPublic?: boolean;
};

type ContainerView = 'hover' | 'error';

const Container = styled.label<{ $view?: ContainerView }>`
    display: flex;
    flex-flow: column;
    ${({ $view }) => css`
        border: 4px dashed
            ${themeable($view === 'error' ? 'error' : 'sidebar.lineColor')};
    `}
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px;
    cursor: pointer;
    position: relative;

    &.hover {
        background: rgba(77, 84, 110, 0.2);
    }

    &.error {
        border-color: ${themeable('error')};
    }
`;

const DragOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const CloudImage = styled.img`
    max-width: 300px;
`;

const HiddenInput = styled.input`
    display: none;
`;

export const FileCloud: FC<FileCloudProps> = ({
    accept,
    onUpload,
    isPublic = false
}) => {
    const [view, setView] = useState<ContainerView>();
    const [loading, setLoading] = useState(false);

    const ref = useRef<HTMLLabelElement>(null);

    const load = useCallback(
        (file: Blob) => {
            setLoading(true);

            uploadFile(
                file,
                isPublic,
                false
            )(
                ({ data }) => {
                    if (data) {
                        onUpload(data);
                    }

                    setLoading(false);
                },
                () => {
                    setView('error');
                    setLoading(false);
                }
            );
        },
        [onUpload, isPublic]
    );

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files ? event.target.files[0] : null;
            if (file) {
                load(file);
            }
        },
        [load]
    );

    const handleDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();

            ref.current?.classList.add(
                event.dataTransfer.types.length === 1 &&
                    event.dataTransfer.types[0] === 'Files'
                    ? 'hover'
                    : 'error'
            );
        },
        []
    );

    const handleDragLeave = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();

            ref.current?.classList.remove('hover', 'error');
        },
        []
    );

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();

            setView(undefined);
            ref.current?.classList.remove('hover', 'error');

            const files = event.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];

                const isValidType =
                    !accept ||
                    FILE_ACCEPT_VALUES[accept].split(',').some((type) => {
                        if (type.includes('*')) {
                            const baseType = type.split('/')[0];
                            return file.type.startsWith(baseType);
                        }
                        return file.type === type.trim();
                    });

                if (!isValidType) {
                    setView('error');
                    toast.error('Неверный формат файла!');
                    return;
                }

                load(file);
            }
        },
        [accept, load]
    );

    return (
        <Container ref={ref} $view={view}>
            <HiddenInput
                type="file"
                accept={accept && FILE_ACCEPT_VALUES[accept]}
                title=""
                onChange={handleFileChange}
            />
            <CloudImage loading="eager" src={imgFileUrl('cloud.png')} />
            <Headline>{loading ? 'Загрузка...' : 'Перетащите файл'}</Headline>

            <DragOverlay
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            />
        </Container>
    );
};
